<script lang="ts">
    import Chart from 'chart.js/auto'
    import type { Plugin } from 'chart.js'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import dayjs from 'dayjs'
    import { onMount } from 'svelte/internal'
    import type { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import { AppointmentStatus, AppointmentStatusFriendly } from '../../modules/schedulerApi/appointments/appointments.enum'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import type { User } from '../../modules/usersApi/users/user'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    let containerOcupacao: HTMLCanvasElement
    let containerStatus: HTMLCanvasElement
    let containerComparecimento: HTMLCanvasElement
    let containerEvolucao: HTMLCanvasElement
    let containerHorarios: HTMLCanvasElement
    let containerAtendente: HTMLCanvasElement
    let show

    let charts: Chart[] = []

    const MUTED = '#666'
    const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

    // cores por semântica de status, não por categoria arbitrária
    const STATUS_COLOR: Record<string, string> = {
        agendamento_criado: '#008FFB',
        agendamento_confirmado: '#00BCD4',
        agendamento_compareceu: '#379E75',
        agendamento_faltou: '#D0483E',
        agendamento_cancelado_usuario: '#9AA0A6',
        agendamento_cancelado_empresa: '#9AA0A6',
        agendamento_remarcado: '#FEB019',
        agendamento_remarcado_descontar: '#FEB019',
    }

    // status que efetivamente ocupam o horário na agenda (um cancelamento libera o slot de volta)
    const OCCUPYING_STATUS = [
        AppointmentStatus.agendamento_criado,
        AppointmentStatus.agendamento_confirmado,
        AppointmentStatus.agendamento_compareceu,
        AppointmentStatus.agendamento_faltou,
        AppointmentStatus.agendamento_remarcado,
        AppointmentStatus.agendamento_remarcado_descontar,
    ]

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    async function getData() {
        try {
            show = true

            const [appointmentsRaw, schedulesRaw, users]: [Appointment[], Schedule[], User[]] = await Promise.all([
                window.api.schedulers.appointments.getAll({ from: dates.from, to: dates.to }),
                window.api.schedulers.schedules.getAll({ discardSlots: false }),
                window.api.users.companies.getCompanyUsers(),
            ])
            // "excluído" é um agendamento apagado; "bloqueado" é o atendente marcando indisponibilidade (não é um atendimento) — nenhum dos dois entra nas estatísticas
            const appointments = appointmentsRaw.filter(
                (a) => a.status !== AppointmentStatus.agendamento_excluido && a.status !== AppointmentStatus.agendamento_bloqueado,
            )
            const schedules = schedulesRaw.filter((s) => s.status === 100)
            console.log('data', appointments, schedules, users)

            charts.forEach((c) => c.destroy())
            charts = []

            generateOcupacao(appointments, schedules)
            generateStatusShare(appointments)
            generateComparecimento(appointments)
            generateEvolucao(appointments)
            generateHorarios(appointments)
            generateAtendente(appointments, users)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    }

    // agrupa o período em dias (ou semanas, se o período for longo)
    function buildBuckets(from: Date, to: Date) {
        const totalDays = Math.ceil((to.getTime() - from.getTime()) / 86400000) + 1
        const stepDays = totalDays > 62 ? 7 : 1
        let buckets: { from: Date; to: Date; label: string }[] = []
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + stepDays)) {
            const bucketFrom = new Date(d)
            const bucketTo = new Date(d)
            bucketTo.setDate(bucketTo.getDate() + stepDays - 1)
            bucketTo.setHours(23, 59, 59, 999)
            buckets.push({
                from: bucketFrom,
                to: bucketTo,
                label: stepDays === 1 ? dayjs(bucketFrom).format('DD/MM') : 'sem ' + dayjs(bucketFrom).format('DD/MM'),
            })
        }
        return buckets
    }

    // soma, em minutos, a janela de funcionamento configurada em "slots" para os dias do período
    function scheduleCapacityMinutes(schedule: Schedule, from: Date, to: Date) {
        let total = 0
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            const dayConfig = schedule.slots?.[d.getDay()]
            if (!dayConfig) continue
            for (const slot of dayConfig.slots || []) {
                if (slot.active) total += slot.end - slot.start
            }
        }
        return total
    }

    // funde intervalos sobrepostos (ex: encaixes na mesma agenda) para que o tempo em paralelo não seja contado 2x
    function mergeIntervals(intervals: { start: number; end: number }[]) {
        if (intervals.length === 0) return []
        const sorted = [...intervals].sort((a, b) => a.start - b.start)
        let merged: { start: number; end: number }[] = [{ ...sorted[0] }]
        for (let i = 1; i < sorted.length; i++) {
            const last = merged[merged.length - 1]
            const current = sorted[i]
            if (current.start <= last.end) {
                last.end = Math.max(last.end, current.end)
            } else {
                merged.push({ ...current })
            }
        }
        return merged
    }

    // minutos realmente ocupados por uma agenda, deduplicando encaixes (agendamentos em paralelo).
    // "compareceu" já está entre os status ocupantes, então um encaixe onde o cliente compareceu conta
    // normalmente — não precisa de tratamento especial, pois a união de intervalos já cobre esse tempo
    // uma única vez, não importa quantos agendamentos (compareceu, criado, confirmado...) se sobrepõem nele
    function occupiedMinutesDeduplicated(appointments: Appointment[], scheduleId: string) {
        const intervals = appointments
            .filter((a) => a.scheduleId === scheduleId && OCCUPYING_STATUS.includes(a.status))
            .map((a) => ({ start: new Date(a.start).getTime(), end: new Date(a.end).getTime() }))
            .filter((i) => i.end > i.start)
        const merged = mergeIntervals(intervals)
        return merged.reduce((total, i) => total + (i.end - i.start) / 60000, 0)
    }

    // taxa de ocupação = minutos efetivamente ocupados (sem contar sobreposição 2x) / minutos disponíveis segundo os slots de funcionamento.
    // não trava em 100%: se passar disso, é sinal real de agendamento fora do horário configurado nos slots
    async function generateOcupacao(appointments: Appointment[], schedules: Schedule[]) {
        const rows = schedules
            .map((schedule) => {
                const capacity = scheduleCapacityMinutes(schedule, dates.from, dates.to)
                const occupied = occupiedMinutesDeduplicated(appointments, schedule.scheduleId)
                return {
                    label: schedule.label,
                    percent: capacity > 0 ? Number(((occupied / capacity) * 100).toFixed(1)) : null,
                }
            })
            .filter((r) => r.percent !== null)
            .sort((a, b) => b.percent - a.percent)

        const chart = new Chart(containerOcupacao, {
            type: 'bar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [{ label: 'Ocupação', data: rows.map((r) => r.percent), backgroundColor: '#008FFB', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 40 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.x.toFixed(1) + '%' } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => val.toFixed(1) + '%' },
                },
                scales: {
                    x: { min: 0, suggestedMax: 100, grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateStatusShare(appointments: Appointment[]) {
        let counts: { key: string; qtde: number }[] = []
        for (const a of appointments) {
            let item = counts.find((c) => c.key === a.status)
            if (!item) {
                item = { key: a.status, qtde: 0 }
                counts.push(item)
            }
            item.qtde += 1
        }
        counts.sort((a, b) => b.qtde - a.qtde)

        const chart = new Chart(containerStatus, {
            type: 'doughnut',
            data: {
                labels: counts.map((c) => AppointmentStatusFriendly[c.key] || c.key),
                datasets: [
                    {
                        data: counts.map((c) => c.qtde),
                        backgroundColor: counts.map((c) => STATUS_COLOR[c.key] || '#9AA0A6'),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val > 0 ? val : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateComparecimento(appointments: Appointment[]) {
        // taxa de comparecimento "pura": mede confiabilidade do cliente (compareceu x faltou), não cancelamento.
        // remarcado/remarcado_descontar entram como falta porque, pela própria definição do status, são
        // um no-show que foi reagendado. cancelamentos (do cliente ou da empresa) ficam de fora — o horário
        // foi desmarcado, não é uma falha de comparecimento.
        const compareceu = appointments.filter((a) => a.status === AppointmentStatus.agendamento_compareceu).length
        const statusFaltou = [AppointmentStatus.agendamento_faltou, AppointmentStatus.agendamento_remarcado, AppointmentStatus.agendamento_remarcado_descontar]
        const faltou = appointments.filter((a) => statusFaltou.includes(a.status)).length
        const concluded = compareceu + faltou
        const percent = concluded > 0 ? (compareceu / concluded) * 100 : 0
        const color = percent >= 90 ? '#379E75' : percent >= 70 ? '#FEB019' : '#D0483E'

        const centerText: Plugin<'doughnut'> = {
            id: 'centerTextComparecimento',
            afterDraw(chart) {
                const { ctx, chartArea } = chart
                const x = (chartArea.left + chartArea.right) / 2
                const y = (chartArea.top + chartArea.bottom) / 2
                ctx.save()
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.font = 'bold 28px sans-serif'
                ctx.fillStyle = color
                ctx.fillText(percent.toFixed(0) + '%', x, y - 8)
                ctx.font = '13px sans-serif'
                ctx.fillStyle = MUTED
                ctx.fillText('Comparecimento', x, y + 18)
                ctx.restore()
            },
        }

        const chart = new Chart(containerComparecimento, {
            type: 'doughnut',
            data: {
                labels: ['Compareceu', 'Faltou'],
                datasets: [{ data: [percent, 100 - percent], backgroundColor: [color, hexToRgba('#999999', 0.2)], borderWidth: 0 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '75%',
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
            },
            plugins: [centerText],
        })
        charts.push(chart)
    }

    async function generateEvolucao(appointments: Appointment[]) {
        const buckets = buildBuckets(dates.from, dates.to)
        const agendados = buckets.map((b) => appointments.filter((a) => new Date(a.start) >= b.from && new Date(a.start) <= b.to).length)
        const compareceram = buckets.map(
            (b) =>
                appointments.filter((a) => a.status === AppointmentStatus.agendamento_compareceu && new Date(a.start) >= b.from && new Date(a.start) <= b.to)
                    .length,
        )

        const chart = new Chart(containerEvolucao, {
            type: 'line',
            data: {
                labels: buckets.map((b) => b.label),
                datasets: [
                    {
                        label: 'Agendados',
                        data: agendados,
                        borderColor: '#008FFB',
                        backgroundColor: hexToRgba('#008FFB', 0.35),
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                    },
                    {
                        label: 'Compareceram',
                        data: compareceram,
                        borderColor: '#379E75',
                        backgroundColor: hexToRgba('#379E75', 0.35),
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.y } },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    function hourBucketLabel(index: number) {
        const start = index * 2
        const end = start + 2
        return String(start).padStart(2, '0') + 'h-' + String(end).padStart(2, '0') + 'h'
    }

    async function generateHorarios(appointments: Appointment[]) {
        let matrix = WEEKDAYS.map(() => Array(12).fill(0))
        for (const a of appointments) {
            const date = new Date(a.start)
            matrix[date.getDay()][Math.floor(date.getHours() / 2)] += 1
        }
        const maxQ = Math.max(1, ...matrix.flat())

        let points: { x: number; y: number; r: number; qtde: number }[] = []
        matrix.forEach((row, wi) => {
            row.forEach((qtde, hi) => {
                if (qtde > 0) points.push({ x: hi, y: wi, r: 4 + (qtde / maxQ) * 16, qtde })
            })
        })

        const chart = new Chart(containerHorarios, {
            type: 'bubble',
            data: { datasets: [{ data: points, backgroundColor: hexToRgba('#008FFB', 0.65), borderColor: '#008FFB' }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const p = ctx.raw as { x: number; y: number; qtde: number }
                                return WEEKDAYS[p.y] + ' ' + hourBucketLabel(p.x) + ': ' + p.qtde + ' agendamento(s)'
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        min: -0.5,
                        max: 11.5,
                        afterBuildTicks: (axis) => {
                            axis.ticks = Array.from({ length: 12 }, (_, i) => ({ value: i }))
                        },
                        ticks: { color: MUTED, callback: (v) => hourBucketLabel(Number(v)) },
                        grid: { display: false },
                    },
                    y: {
                        min: -0.5,
                        max: 6.5,
                        reverse: true,
                        afterBuildTicks: (axis) => {
                            axis.ticks = Array.from({ length: 7 }, (_, i) => ({ value: i }))
                        },
                        ticks: { color: MUTED, callback: (v) => WEEKDAYS[Number(v)] || '' },
                        grid: { display: false },
                    },
                },
            },
        })
        charts.push(chart)
    }

    // appointment.userId é quem criou o agendamento (o usuário logado), não necessariamente quem atende.
    // o atendente real está em cada item de appointment.items. um agendamento pode ter mais de um atendente,
    // mas se o mesmo atendente aparece em vários itens do mesmo agendamento (ex: cabelo e barba), conta 1 só —
    // por isso deduplico os atendentes dentro de cada agendamento antes de somar
    async function generateAtendente(appointments: Appointment[], users: User[]) {
        let counts: { userId: string; qtde: number }[] = []
        for (const a of appointments) {
            const attendantIds = new Set<string>()
            for (const item of a.items || []) {
                if (item.userId) attendantIds.add(item.userId)
            }
            for (const userId of attendantIds) {
                let counted = counts.find((c) => c.userId === userId)
                if (!counted) {
                    counted = { userId, qtde: 0 }
                    counts.push(counted)
                }
                counted.qtde += 1
            }
        }
        counts.sort((a, b) => b.qtde - a.qtde)
        const top = counts.slice(0, 15)

        const chart = new Chart(containerAtendente, {
            type: 'bar',
            data: {
                labels: top.map((c) => users.find((u) => u.userId === c.userId)?.userName || 'Não informado'),
                datasets: [{ label: 'Agendamentos', data: top.map((c) => c.qtde), backgroundColor: '#775DD0', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 30 } },
                plugins: {
                    legend: { display: false },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(async () => {
        await getData()
    })
</script>

<div class="base-dash sales-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
    </Zact>

    <ZContainer class="h-space">
        <Zframe
            size={12}
            title="Taxa de ocupação por agenda"
            headline="Percentual do horário de funcionamento de cada agenda (definido nos 'slots') que está ocupado por agendamentos no período. Encaixes desconsideramos o tempo sobreposto. Cancelamentos e horários bloqueados não contam como ocupação. Caso a ocupação ultrapasse 100%, é sinal de agendamentos fora do horário configurado nos slots. Agendas sem horário de funcionamento configurado não aparecem aqui."
        >
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerOcupacao}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Agendamentos por status"
            headline="Distribuição dos agendamentos do período por status (agendado, confirmado, compareceu, faltou, cancelado, remarcado)."
        >
            <div class="chart-box chart-box_fill">
                <canvas bind:this={containerStatus}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Taxa de comparecimento"
            headline="Percentual de comparecimento em relação aos agendamentos com desfecho conhecido (compareceu, faltou, ou faltou e foi remarcado). Mede a confiabilidade do cliente em aparecer — agendamentos ainda em aberto ou cancelados (pelo cliente ou pela empresa) não entram nesse cálculo, pois não são uma falta."
        >
            <div class="chart-box chart-box_fill">
                <canvas bind:this={containerComparecimento}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={12}
            title="Evolução de agendamentos"
            headline="Quantidade de agendamentos marcados (inclui os já cancelados, por qualquer uma das partes) x quantidade que efetivamente compareceu, por dia (ou semana, em períodos mais longos). Em datas futuras ou muito recentes, o gap entre as linhas reflete agendamentos que ainda não tiveram desfecho registrado, não necessariamente falta."
        >
            <div class="chart-box">
                <canvas bind:this={containerEvolucao}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Horários de maior movimento"
            headline="Quantidade de agendamentos por dia da semana e faixa de horário (bolhas maiores = mais agendamentos), com base no horário de início de cada agendamento."
        >
            <div class="chart-box">
                <canvas bind:this={containerHorarios}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Agendamentos por atendente"
            headline="Ranking da quantidade de agendamentos no período por atendente responsável pelo serviço, não pelo usuário que criou o agendamento. Um agendamento pode contar para mais de um atendente, mas o mesmo atendente conta só uma vez por agendamento, mesmo que tenha feito mais de um serviço nele."
        >
            <div class="chart-box">
                <canvas bind:this={containerAtendente}></canvas>
            </div>
        </Zframe>
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 380px;
    }
    .chart-box_fill {
        height: 100%;
        min-height: 320px;
    }
</style>
